import { MacroResponse } from "types/misc";

export enum ProcessState {
  IDLE = "IDLE",
  INITIALIZING = "INITIALIZING",
  QUEUED = "QUEUED",
  PROCESSING = "PROCESSING",
  COMPLETE = "COMPLETE",
  ERROR = "ERROR",
}

export const ProcessTypeMap = {
  ExcelExport: "ExcelExportProcess",
  ExcelImport: "ExcelImportProcess",
  ImportProductLineDBJson: "ImportProductLineDBJsonProcess",
  ImportProductLineRulesJson: "ImportProductLineRulesJsonProcess",
  UpdateUnitSchema: "UpdateUnitSchemaProcess",
  ValidateProductLine: "ValidationProcess",
  UnitTestOnly: "UnitTestOnly",
} as const;

export type ProcessType = (typeof ProcessTypeMap)[keyof typeof ProcessTypeMap];

//Union of various Process sub-classes
export type Process =
  | ExcelExportProcess
  | ExcelImportProcess
  | ImportProductLineDBJsonProcess
  | ImportProductLineRulesJsonProcess
  | UpdateUnitSchemaProcess
  | ValidationProcess
  | ProcessBase; // safety net

export interface ProcessBase {
  id: string;
  userId: string;
  productLineId: string;
  dateCreated: string;
  dateUpdated: string;
  processType: ProcessType;
  processState: ProcessState;
  message: string;
  percentComplete?: number;
  dbSchemaVersion: number;
}

export interface ExcelExportProcess extends ProcessBase {
  processType: typeof ProcessTypeMap.ExcelExport;
  fileName: string;
}

export interface ExcelImportProcess extends ProcessBase {
  processType: typeof ProcessTypeMap.ExcelImport;

  completedWorksheets: number;
  totalWorksheets: number;
  parsedPropertyCount: number;
  parsedTableCount: number;
  resolvedPropertyCount: number;
  resolvedTableCount: number;
  importablePropertyCount: number;
  importableTableCount: number;
  importedPropertyCount: number;
  importedTableCount: number;
  createdPropertyCount: number;
  createdTableCount: number;
  replacedPropertyCount: number;
  replacedTableCount: number;
  skippedPropertyCount: number;
  skippedTableCount: number;
  createdProperties: string[];
  replacedProperties: string[];
  skippedProperties: ExcelImportSkippedProperties[];
  productlineNumValidations: number;
  propertyNumValidations: number;
  tableNumValidations: number;
}

export interface ExcelImportSkippedProperties {
  id: string;
  name: string;
  reason: string;
  excelOrigin: ExcelOrigin;
  errors: string[];
}

export interface ExcelOrigin {
  workbook: string;
  worksheet: string;
  row: number;
  column: number | null;
}

export interface ImportProductLineDBJsonProcess extends ProcessBase {
  processType: typeof ProcessTypeMap.ImportProductLineDBJson;
}

export interface ImportProductLineRulesJsonProcess extends ProcessBase {
  processType: typeof ProcessTypeMap.ImportProductLineRulesJson;
}

export interface UpdateUnitSchemaProcess extends ProcessBase {
  processType: typeof ProcessTypeMap.UpdateUnitSchema;
  macroResponse: MacroResponse;
}

export interface ValidationProcess extends ProcessBase {
  processType: typeof ProcessTypeMap.ValidateProductLine;

  productLineTotal: number;
  productLineNumComplete: number;
  productLineNumValidations: number;

  propertyTotal: number;
  propertyNumComplete: number;
  propertyNumValidations: number;

  tableTotal: number;
  tableNumComplete: number;
  tableNumValidations: number;
}
